import { serverOptionsFilter } from "@dehub/theme";
import { Box, Button, Flex, Grid, Paper, Select, Text, Textarea, ThemeIcon } from "@mantine/core";
import { MonthPickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { CalendarIcon, CheckIcon, CoinIcon, NoteIcon, UserIcon } from "@phosphor-icons/react";
import dayjs from "dayjs";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { NetworkSettlementApi, ReconciliationApi } from "../../apis";
import { Page, PageHeader } from "../../components/layout/Page";
import { usePermission } from "../../components/permission";
import { Breadcrumb } from "../../components/layout/breadcrumb";
import { message } from "../../utils/Message";
import { handleError } from "../../utils/error-handler";
import { renderBusinessName } from "../../utils/render-business-name";
import classes from "./Create.module.css";

type FormValues = {
  buyerBusinessId: string | null;
  periodMonth: Date | null;
  currency: string;
  sellerNotes: string;
};

export const StatementCreate = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["reconciliation", "common"]);
  const breadcrumbs = [
    { label: t("breadcrumb.list"), to: "/settlement" },
    { label: t("breadcrumb.create") },
  ];

  const form = useForm<FormValues>({
    mode: "controlled",
    initialValues: {
      buyerBusinessId: null,
      periodMonth: null,
      currency: "MNT",
      sellerNotes: "",
    },
  });
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [buyerSearch, setBuyerSearch] = useState("");

  const INV_SETT_SEND = usePermission("INV_SETT_SEND");

  const { data: buyerList } = useSWR(INV_SETT_SEND.hasAccess ? `swr.reconciliation.buyers.${buyerSearch}` : null, () => NetworkSettlementApi.business_select({ query: buyerSearch }), {
    revalidateOnFocus: false,
  });

  const buyerOptions = (buyerList?.rows || buyerList || []).map((b: any) => ({
    value: b.id || b.businessId,
    label: renderBusinessName(b, "") || b.name || "-",
  }));

  const handleSubmit = async () => {
    if (!form.values.buyerBusinessId) {
      message.error(t("create.errorCounterparty"));
      return;
    }
    if (!form.values.periodMonth) {
      message.error(t("create.errorPeriod"));
      return;
    }

    setSubmitting(true);
    try {
      const res = await ReconciliationApi.create({
        buyerBusinessId: form.values.buyerBusinessId,
        month: dayjs(form.values.periodMonth).format("YYYY-MM"),
        currency: form.values.currency,
        sellerNotes: form.values.sellerNotes.trim() || undefined,
      });
      message.success(t("success.sent"));
      navigate(`/settlement/${res?.id || ""}`);
    } catch (error) {
      handleError(error, t("error.send"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!INV_SETT_SEND.hasAccess) {
    return (
      <Page>
        <PageHeader breadcrumb={<Breadcrumb data={breadcrumbs} />} title={t("newReconciliation")} />
        {INV_SETT_SEND.accessDenied()}
      </Page>
    );
  }

  return (
    <Page>
      <PageHeader
        breadcrumb={<Breadcrumb data={breadcrumbs} />}
        title={t("newReconciliation")}
        subtitle={t("create.formSubtitle")}
      />

      {/* Section 1: Харилцагч */}
      <Paper className={classes.section} mb="sm">
        <Flex align="center" gap={8} mb="md">
          <ThemeIcon size={26} radius="sm" variant="light" color="gray">
            <UserIcon size={14} />
          </ThemeIcon>
          <Text fw={600} fz={14}>{t("create.selectCounterparty")}</Text>
        </Flex>
        <Grid gap="lg">
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Box>
              <Text fz="sm" c="dimmed" mb={4}>{t("detail.buyer")}</Text>
              <Select
                placeholder={t("create.businessPlaceholder")}
                data={buyerOptions}
                {...form.getInputProps("buyerBusinessId")}
                searchable
                filter={serverOptionsFilter}
                onSearchChange={setBuyerSearch}
                clearable
                nothingFoundMessage={t("common:notFound")}
                size="sm"
                radius="md"
                styles={{ input: { background: "var(--mantine-color-gray-0)", border: "1.5px solid var(--mantine-color-gray-2)" } }}
              />
            </Box>
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Section 2: Хугацаа & Валют */}
      <Paper className={classes.section} mb="sm">
        <Flex align="center" gap={8} mb="md">
          <ThemeIcon size={26} radius="sm" variant="light" color="green">
            <CoinIcon size={14} />
          </ThemeIcon>
          <Text fw={600} fz={14}>{t("create.periodAndCurrency")}</Text>
        </Flex>
        <Grid gap="lg">
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Box>
              <Text fz="sm" c="dimmed" mb={4}>{t("detail.period")}</Text>
              <MonthPickerInput
                placeholder={t("monthPlaceholder")}
                leftSection={<CalendarIcon size={14} />}
                {...form.getInputProps("periodMonth")}
                valueFormat="YYYY-MM"
                clearable
                size="sm"
                radius="md"
                styles={{ input: { background: "var(--mantine-color-gray-0)", border: "1.5px solid var(--mantine-color-gray-2)" } }}
              />
            </Box>
          </Grid.Col>
          <Grid.Col span={{ xs: 12, sm: 6, md: 4 }}>
            <Box>
              <Text fz="sm" c="dimmed" mb={4}>{t("detail.currency")}</Text>
              <Select
                placeholder={t("create.currencyPlaceholder")}
                data={[
                  { value: "MNT", label: t("create.currencyMNT") },
                  { value: "USD", label: t("create.currencyUSD") },
                ]}
                {...form.getInputProps("currency")}
                size="sm"
                radius="md"
                styles={{ input: { background: "var(--mantine-color-gray-0)", border: "1.5px solid var(--mantine-color-gray-2)" } }}
              />
            </Box>
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Section 3: Тэмдэглэл */}
      <Paper className={classes.section} mb="sm">
        <Flex align="center" gap={8} mb="md">
          <ThemeIcon size={26} radius="sm" variant="light" color="yellow">
            <NoteIcon size={14} />
          </ThemeIcon>
          <Text fw={600} fz={14}>{t("notes.title")}</Text>
        </Flex>
        <Grid gap="lg">
          <Grid.Col span={{ xs: 12, md: 8 }}>
            <Box>
              <Text fz="sm" c="dimmed" mb={4}>{t("notes.buyerNote")}</Text>
              <Textarea
                placeholder={t("notes.buyerNotePlaceholder")}
                {...form.getInputProps("sellerNotes")}
                minRows={3}
                maxRows={6}
                size="sm"
                radius="md"
                styles={{ input: { background: "var(--mantine-color-gray-0)", border: "1.5px solid var(--mantine-color-gray-2)" } }}
              />
            </Box>
          </Grid.Col>
        </Grid>
      </Paper>

      {/* Action Buttons */}
      <Flex justify="flex-end" gap="sm" mt="md">
        <Button variant="default" onClick={() => navigate(-1)} radius="md">
          {t("common:cancel")}
        </Button>
        <Button leftSection={<CheckIcon size={16} />} onClick={handleSubmit} loading={submitting} radius="md">
          {t("create.submit")}
        </Button>
      </Flex>
    </Page>
  );
};
